// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    cartlineContainerStyles,
    cartlineContainerStylesExtraCol,
} from "@vanmeter/App/Components/Cart/CartLineContainer";
import { CART_BREAKPOINT } from "@vanmeter/XDL/constants";
import { avoidBreakInsidePrintElement, color, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

cartlineContainerStyles.container = {
    css: css`
        display: grid;
        grid-column-gap: 16px;
        width: 100%;
        background: ${color("common.white")};
        border-bottom: 1px solid ${color("common.border")};
        padding: 12px 8px;

        // Wide Screens View
        grid-template-columns: 96px minmax(220px, 1fr) minmax(0, 184px) minmax(0, 137px) minmax(0, 184px) 16px;

        // Desktop View
        ${xScreen("lte", "lg")} {
            grid-template-columns: 96px minmax(220px, 1fr) minmax(0, 154px) minmax(0, 114px) minmax(0, 154px) 16px;
        }

        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-template-columns: 96px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 14px);
            grid-column-gap: 8px;
            grid-row-gap: 12px;
            padding: 12px 0;
        }
        ${avoidBreakInsidePrintElement}
    `,
};
cartlineContainerStyles.image = cartlineContainerStylesExtraCol.image = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 4;
        display: flex;
        flex-direction: column;
        gap: 8px;
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-row: 1 / 2;
        }
    `,
};
cartlineContainerStyles.description = cartlineContainerStylesExtraCol.description = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        position: relative;
        margin-bottom: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;

        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 2 / 5;
            grid-row: 1 / 2;
            margin-bottom: 0;
        }
    `,
};

cartlineContainerStyles.price = cartlineContainerStylesExtraCol.price = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 4px;
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
            padding-top: 0;
        }
    `,
};
cartlineContainerStyles.qty = cartlineContainerStylesExtraCol.qty = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        display: flex;
        align-items: flex-start;
        &:not(:has(input)) {
            padding-top: 4px;
        }
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 3;
            grid-row: 3 / 4;
            justify-content: flex-start;
            flex-direction: column;
            && {
                padding: 0;
            }
        }
    `,
};
cartlineContainerStyles.total = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 4px;
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 3 / 5;
            grid-row: 3 / 4;
            padding-top: 0;
        }
    `,
};
cartlineContainerStyles.remove = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 1 / 2;
        padding-top: 6px;
        margin-left: auto;
        ${xScreen("lte", CART_BREAKPOINT)} {
            padding-top: 0;
            grid-row: 1 / 2;
            grid-column: 5 / 5;
        }
    `,
};
cartlineContainerStyles.actions = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 2 / 3;
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        & > div {
            width: fit-content;
        }
        &:empty {
            display: none;
        }
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 6;
            grid-row: 4 / 5;
            gap: 12px;
            padding-left: 0;
        }
    `,
};
cartlineContainerStyles.notes = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 3 / 4;
        position: relative;
        display: flex;
        &:empty {
            display: none;
        }
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 6;
            grid-row: 5 / 6;
            margin-top: 0;
        }
    `,
};

// ExtraCol Cartline Styles
cartlineContainerStylesExtraCol.container = {
    css: css`
        display: grid;
        grid-column-gap: 16px;
        width: 100%;
        background: ${color("common.white")};
        border-bottom: 1px solid ${color("common.border")};
        padding: 12px 14px;

        // Wide Screens View
        grid-template-columns:
            96px minmax(220px, 1fr) minmax(0, 150px) minmax(0, 120px) minmax(0, 120px) minmax(0, 150px)
            16px;

        // Desktop View
        ${xScreen("lte", "lg")} {
            grid-template-columns:
                96px minmax(220px, 1fr) minmax(0, 120px) minmax(0, 130px) minmax(0, 100px) minmax(0, 120px)
                16px;
        }

        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-template-columns: 96px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 14px);
            grid-column-gap: 8px;
            grid-row-gap: 12px;
            padding: 12px 0;
        }
        ${avoidBreakInsidePrintElement}
    `,
};

cartlineContainerStylesExtraCol.extraCol = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 3 / 5;
            grid-row: 3 / 4;
            justify-content: flex-start;
        }
    `,
};

cartlineContainerStylesExtraCol.total = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 4px;
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 3;
            grid-row: 4 / 5;
            padding-top: 0;
        }
    `,
};
cartlineContainerStylesExtraCol.remove = {
    css: css`
        grid-column: 7 / 8;
        grid-row: 1 / 2;
        padding-top: 6px;
        ${xScreen("lte", CART_BREAKPOINT)} {
            padding-top: 0;
            grid-row: 1 / 2;
            grid-column: 5 / 5;
        }
    `,
};
cartlineContainerStylesExtraCol.actions = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 2 / 3;
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        &:empty {
            display: none;
        }
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 5;
            grid-row: 5 / 6;
            gap: 12px;
        }
    `,
};
cartlineContainerStylesExtraCol.notes = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 3 / 4;
        position: relative;
        &:empty {
            display: none;
        }
        ${xScreen("lte", CART_BREAKPOINT)} {
            grid-column: 1 / 4;
            grid-row: 6 / 7;
            padding-left: 0;
        }
    `,
};
